/**
 * Arm B's Strip A, "Every reflection, ready to revisit" (#1070), shown right
 * after Pain points: a demo of the dashboard and the patterns it surfaces.
 *
 * Built from the approved markup in `Homepage B.dc.html`, section `03 NEW
 * Strip A`. The scene is composed HTML rather than a screenshot, so it stays
 * sharp and translatable. It has two layouts in the markup at once, and the
 * stylesheet shows one: at 700px and wider a laptop dashboard with the
 * Patterns column beside it, below 700px a tall phone dashboard with the
 * Patterns block under it. Each scene is one `role="img"` with a catalog
 * label; its inner UI is `aria-hidden`.
 */
import { useTranslations } from "next-intl";

import DemoPicture from "./DemoPicture";
import { DASHBOARD_STATS, DASHBOARD_TILES, DEMO_NAME, PATTERN_DECK } from "./homepageDemo";
import { accent } from "../accent";

/** `sizes` of a laptop grid tile: half the 640px dashboard, less its padding. */
const TILE_SIZES_WIDE = "290px";
/** `sizes` of a phone grid tile, which is cropped by height: ~155px wide. */
const TILE_SIZES_NARROW = "155px";
/** `sizes` of a deck card: 128px less its 4px border on each side. */
const DECK_SIZES = "120px";

/** The teal orb beside the AI's labels. */
const Orb = () => <span className="figma-b__orb" />;

/** The Reflect logo and wordmark in the scene's own top bar. */
function SceneBrand() {
  const t = useTranslations("web.home");
  return (
    <span className="figma-b__scene-brand">
      <img src="/static/reflect/ReflectLogo.svg" alt="" className="figma-b__scene-logo" />
      <span className="figma-b__scene-wordmark">{t("brand")}</span>
    </span>
  );
}

/**
 * The thumbnail grid.
 *
 * @param {object} props
 * @param {"wide" | "narrow"} props.layout - which dashboard it sits in.
 */
function TileGrid({ layout }) {
  const t = useTranslations("web.homeB.scene");
  return (
    <div className={`figma-b__tiles figma-b__tiles--${layout}`}>
      {DASHBOARD_TILES.map((tile) => (
        <div
          key={tile.id}
          className="figma-b__tile"
          style={{ gridRow: `span ${tile.span}` }}
          data-tile={tile.id}
        >
          <DemoPicture
            scene={tile.scene}
            sizes={layout === "wide" ? TILE_SIZES_WIDE : TILE_SIZES_NARROW}
          />
          <span className="figma-b__tile-shade" />
          <span className="figma-b__tile-dot" />
          <span className="figma-b__tile-title">{t(`tiles.${tile.id}`)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * The "one thing I noticed" card.
 *
 * @param {object} props
 * @param {"wide" | "narrow"} props.layout - the laptop's carries a Play button.
 */
function InsightCard({ layout }) {
  const t = useTranslations("web.homeB.scene");
  return (
    <div className={`figma-b__insight figma-b__insight--${layout}`}>
      <div className="figma-b__ai-row">
        <Orb />
        <span className="figma-b__ai-label">{t("noticedLabel")}</span>
      </div>
      <div className="figma-b__insight-body">{t("noticedBody")}</div>
      <div className="figma-b__read-more">{t("readMore")}</div>
      {layout === "wide" ? <div className="figma-b__play">{t("play")}</div> : null}
    </div>
  );
}

/**
 * The progress pill to the next pattern.
 *
 * @param {object} props
 * @param {{ done: number, total: number }} props.progress - steps done of total.
 * @param {"wide" | "narrow"} props.layout - which dashboard it sits in.
 */
function Progress({ progress, layout }) {
  const t = useTranslations("web.homeB.scene");
  return (
    <div className={`figma-b__progress figma-b__progress--${layout}`}>
      <div className="figma-b__progress-label">{t("progress", progress)}</div>
      <div className="figma-b__progress-track">
        <div
          className="figma-b__progress-fill"
          style={{ width: `${(progress.done / progress.total) * 100}%` }}
        />
      </div>
    </div>
  );
}

/** The "Your reflections · 133" line above the grid. */
function ReflectionsHeader() {
  const t = useTranslations("web.homeB.scene");
  return (
    <div className="figma-b__reflections-head">
      <span>{t("yourReflections")}</span>
      <span className="figma-b__reflections-count">{DASHBOARD_STATS.total}</span>
    </div>
  );
}

/**
 * The Patterns column: a label, the rotated card deck and the pattern card.
 * Decorative: the scene's label describes it.
 *
 * @param {object} props
 * @param {"wide" | "narrow"} props.layout - which scene it sits in.
 */
function Patterns({ layout }) {
  const t = useTranslations("web.homeB.scene");
  return (
    <div className={`figma-b__patterns figma-b__patterns--${layout}`} aria-hidden="true">
      <span className="figma-b__scene-label">{t("patternsLabel")}</span>
      <div className="figma-b__deck">
        {PATTERN_DECK.map((card) => (
          <div
            key={card.scene}
            className="figma-b__deck-card"
            style={{
              top: `${card.top}px`,
              left: `${card.left}px`,
              transform: `rotate(${card.rotate}deg)`,
            }}
          >
            <DemoPicture scene={card.scene} sizes={DECK_SIZES} />
          </div>
        ))}
      </div>
      <div className="figma-b__pattern">
        <div className="figma-b__pattern-head">
          <span className="figma-b__ai-row">
            <Orb />
            <span className="figma-b__ai-label">{t("patternLabel")}</span>
          </span>
          <span className="figma-b__pattern-tag">{t("patternTag")}</span>
        </div>
        <div className="figma-b__pattern-body">{t("patternBody")}</div>
        <div className="figma-b__read-more figma-b__read-more--lg">{t("readMore")}</div>
      </div>
    </div>
  );
}

/** The scene at 700px and wider: the laptop dashboard and the Patterns column. */
function WideScene() {
  const t = useTranslations("web.homeB");
  const s = useTranslations("web.homeB.scene");
  return (
    <div
      className="figma-b__scene figma-b__scene--wide"
      role="img"
      aria-label={t("stripA.sceneLabelWide")}
      data-test-id="landing-strip-a-scene-wide"
    >
      <div className="figma-b__dash-col" aria-hidden="true">
        <span className="figma-b__scene-label">{s("dashboardLabel")}</span>
        <div className="figma-b__laptop">
          <div className="figma-b__topbar figma-b__topbar--wide">
            <SceneBrand />
            <span className="figma-b__topbar-nav">
              <span>{s("nav.dashboard")}</span>
              <span>{s("nav.feedback")}</span>
              <span>{s("nav.account")}</span>
            </span>
          </div>
          <div className="figma-b__dash-body figma-b__dash-body--wide">
            <div className="figma-b__greeting-row">
              <div>
                <div className="figma-b__greeting figma-b__greeting--wide">
                  {s("greetingWide", { name: DEMO_NAME })}
                </div>
                <div className="figma-b__in-view figma-b__in-view--wide">
                  {s("inView", { count: DASHBOARD_STATS.inView })}
                </div>
              </div>
              <span className="figma-b__write figma-b__write--wide">{s("write")}</span>
            </div>
            <Progress progress={DASHBOARD_STATS.progressWide} layout="wide" />
            <InsightCard layout="wide" />
            <ReflectionsHeader />
            <TileGrid layout="wide" />
          </div>
          <div className="figma-b__fade figma-b__fade--wide" />
        </div>
      </div>
      <Patterns layout="wide" />
    </div>
  );
}

/** The scene below 700px: the tall phone dashboard with Patterns under it. */
function NarrowScene() {
  const t = useTranslations("web.homeB");
  const s = useTranslations("web.homeB.scene");
  return (
    <div
      className="figma-b__scene figma-b__scene--narrow"
      role="img"
      aria-label={t("stripA.sceneLabelNarrow")}
      data-test-id="landing-strip-a-scene-narrow"
    >
      <div className="figma-b__dash-col figma-b__dash-col--narrow" aria-hidden="true">
        <span className="figma-b__scene-label">{s("dashboardLabel")}</span>
        <div className="figma-b__phone-dash">
          <div className="figma-b__topbar figma-b__topbar--narrow">
            <SceneBrand />
            <span className="figma-b__burger">
              <span />
              <span />
              <span />
            </span>
          </div>
          <div className="figma-b__dash-body figma-b__dash-body--narrow">
            <div className="figma-b__greeting figma-b__greeting--narrow">
              {s.rich("greetingNarrow", {
                name: DEMO_NAME,
                line: (chunks) => <span className="figma-b__line">{chunks}</span>,
              })}
            </div>
            <div className="figma-b__in-view figma-b__in-view--narrow">
              {s("inView", { count: DASHBOARD_STATS.inView })}
            </div>
            <span className="figma-b__write figma-b__write--narrow">{s("write")}</span>
            <Progress progress={DASHBOARD_STATS.progressNarrow} layout="narrow" />
            <InsightCard layout="narrow" />
            <ReflectionsHeader />
            <TileGrid layout="narrow" />
          </div>
          <div className="figma-b__fade figma-b__fade--narrow" />
        </div>
      </div>
      <Patterns layout="narrow" />
    </div>
  );
}

/**
 * Strip A.
 *
 * @param {object} props
 * @param {string} props.ctaLabel - the CTA's label, as arm A words its primary CTA.
 * @param {() => void} props.onCta - called when the CTA is clicked.
 */
const StripRevisit = ({ ctaLabel, onCta }) => {
  const t = useTranslations("web.homeB.stripA");
  return (
    <section
      className="figma-a__section figma-b__strip figma-b__strip--revisit"
      data-test-id="landing-strip-a"
    >
      <div className="figma-a__container">
        <div className="figma-b__header">
          <span className="figma-a__eyebrow">{t("eyebrow")}</span>
          <h2 className="figma-a__section-title">{t.rich("title", { accent })}</h2>
          <p className="figma-a__section-body">{t("body")}</p>
        </div>
        <WideScene />
        <NarrowScene />
        <p className="figma-b__caption figma-b__caption--revisit">{t("caption")}</p>
        <div className="figma-b__cta">
          <button
            className="figma-a__button"
            type="button"
            data-test-id="landing-cta-strip-a"
            onClick={onCta}
          >
            {ctaLabel}
          </button>
          <span className="figma-b__trust">{t("trust")}</span>
        </div>
      </div>
    </section>
  );
};

export default StripRevisit;
